import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Flex,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router";

import { ChangeActorDisplay } from "@/common/components/ChangeActorDisplay";
import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import { IcoCheck, IcoFeatureAutoCleanup } from "@/common/components/Icons";
import { formatRelativeTime } from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { useSlugParam } from "@/common/hooks/useParam";
import { CheckListItem } from "@/feature/components/ArchivingComponents";
import { LinearCleanupIssueLink } from "@/feature/components/LinearCleanupIssueLink";
import { PullRequestLink } from "@/feature/components/PullRequestLink";
import { useFeatureArchivingChecks } from "@/feature/data/useFeatureArchivingChecks";
import { useFeatureData } from "@/feature/data/useFeatureData";
import {
  useArchiveFeature,
  useFeatureUpdateMutation,
} from "@/feature/data/useFeatureUpdateMutation";
import { useLatestLinearCleanupIssue } from "@/feature/data/useLatestLinearCleanupIssue";
import { useLatestPullRequest } from "@/feature/hooks/useLatestPullRequest";
import { useFlag } from "@reflag/react-sdk";
import { getCleanupPromptLink } from "@reflag/shared/cleanupPrompt";
import { ArchivingChecks, LinearCleanupIssue } from "@reflag/shared/featureAPI";
import { FeatureSettingsUrl, GlobalSettingsUrl } from "@reflag/shared/urls";
import pluralize from "@reflag/shared/utils/pluralize";

export function ArchivingModal({
  featureId,
  modalDisclosure,
}: {
  featureId: string;
  modalDisclosure: ReturnType<typeof useDisclosure>;
}) {
  const { env } = useEnvironment();
  const toast = useToast();
  const errorToast = useErrorToast();
  const archiveConfirmationDisclosure = useDisclosure();
  const manualCodeCleanupConfirmationDisclosure = useDisclosure();

  const { requestFeedback } = useFlag("archiving");

  const { data: archivingChecks } = useFeatureArchivingChecks(featureId);
  const { data: feature } = useFeatureData(featureId);
  const cleanupIssue = useLatestLinearCleanupIssue(featureId, feature);

  const { mutate: updateFeature } = useFeatureUpdateMutation(undefined, {
    onSuccess: () => {
      toast({
        title: "Flag cleanup settings updated",
        status: "success",
      });
    },
    onError: () => {
      errorToast({
        title: "Couldn't update flag cleanup settings",
      });
    },
  });
  const archive = useArchiveFeature(featureId);

  const archiveAnywayHandler = useCallback(() => {
    archiveConfirmationDisclosure.onOpen();
  }, [archiveConfirmationDisclosure]);

  const [passedChecks, totalChecks] = useMemo(() => {
    const checks = [
      archivingChecks?.isStale,
      archivingChecks?.codeRefsRemovalStatus,
      archivingChecks?.lastCheckStatus,
    ];
    return [checks.filter(Boolean).length, checks.length];
  }, [archivingChecks]);

  const archiveConfirmationHandler = useCallback(() => {
    archive();
    archiveConfirmationDisclosure.onClose();
    modalDisclosure.onClose();
  }, [archive, archiveConfirmationDisclosure, modalDisclosure]);

  const manualCodeCleanupHandler = useCallback(() => {
    if (feature) {
      updateFeature({
        id: feature.id,
        isCodebaseCleanedManually: true,
      });
      manualCodeCleanupConfirmationDisclosure.onClose();
    }
  }, [feature, updateFeature, manualCodeCleanupConfirmationDisclosure]);

  const undoManualCodeCleanupHandler = useCallback(() => {
    if (feature) {
      updateFeature({
        id: feature.id,
        isCodebaseCleanedManually: false,
      });
    }
  }, [feature, updateFeature]);

  const archiveNowHandler = useCallback(() => {
    archive();
    modalDisclosure.onClose();
  }, [archive, modalDisclosure]);

  const handleAutoArchiveToggle = useCallback(() => {
    if (feature) {
      updateFeature({
        id: feature.id,
        autoArchive: !feature.autoArchive,
      });
    }
  }, [feature, updateFeature]);

  const handleManualCodeCleanup = useCallback(() => {
    manualCodeCleanupConfirmationDisclosure.onOpen();
  }, [manualCodeCleanupConfirmationDisclosure]);

  const handleFeedbackClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      requestFeedback({
        title: "How do you like the Archive Guide?",
        position: {
          type: "POPOVER",
          anchor: e.currentTarget,
        },
        openWithCommentVisible: true,
      });
    },
    [requestFeedback],
  );

  const allChecksPassedColor = useColorModeValue("green.500", "green.400");
  const hasAllChecksPassed = archivingChecks && passedChecks === totalChecks;

  const isAutoArchiveAvailable =
    !!archivingChecks &&
    !archivingChecks.checksPassed &&
    !archivingChecks.isArchived;

  return (
    <Modal
      isOpen={modalDisclosure.isOpen}
      trapFocus={false}
      onClose={modalDisclosure.onClose}
    >
      <ModalOverlay />
      <ConfirmationDialog
        description={
          <Text>
            Some of the conditions to safely archive the flag are not met. Are
            you sure you want to proceed?
          </Text>
        }
        isOpen={archiveConfirmationDisclosure.isOpen}
        title="Archive flag"
        onCancel={archiveConfirmationDisclosure.onClose}
        onClose={archiveConfirmationDisclosure.onClose}
        onConfirm={archiveConfirmationHandler}
      />
      <ConfirmationDialog
        description={
          <Text>
            Are you sure you want to mark the flag code as removed manually?
          </Text>
        }
        isOpen={manualCodeCleanupConfirmationDisclosure.isOpen}
        title="Mark flag code as removed manually"
        onCancel={manualCodeCleanupConfirmationDisclosure.onClose}
        onClose={manualCodeCleanupConfirmationDisclosure.onClose}
        onConfirm={manualCodeCleanupHandler}
      />
      <ModalContent gap={6}>
        <ModalHeader
          alignItems="center"
          display="flex"
          justifyContent="space-between"
        >
          Archive guide
          <HStack spacing={2}>
            <Button
              colorScheme="brand"
              size="sm"
              variant="ghost"
              onClick={handleFeedbackClick}
            >
              Give feedback
            </Button>
            <ModalCloseButton position="static" />
          </HStack>
        </ModalHeader>
        <ModalBody fontSize="sm">
          {feature && archivingChecks && (
            <Flex direction="column" gap={4}>
              <ArchivingChecklist
                archivingChecks={archivingChecks}
                linearCleanupIssue={cleanupIssue}
                cleanupPromptLink={getCleanupPromptLink({
                  featureName: feature.name,
                  featureKey: feature.key,
                })}
                onManualCodeCleanupClick={handleManualCodeCleanup}
                onUndoManualCodeCleanupClick={undoManualCodeCleanupHandler}
              />
              {isAutoArchiveAvailable && (
                <Flex align="center" gap={2}>
                  <Switch
                    isChecked={feature.autoArchive}
                    isDisabled={archivingChecks.isArchived}
                    size="sm"
                    onChange={handleAutoArchiveToggle}
                  />
                  <Text fontSize="sm">Auto-archive when all checks pass</Text>
                </Flex>
              )}
              {archivingChecks?.isPermanent ? (
                <Alert status="warning">
                  The flag is permanent and won&apos;t be suggested for
                  archival.
                </Alert>
              ) : (
                <Text color="dimmed">
                  <Link as={RouterLink} to={FeatureSettingsUrl(env, feature)}>
                    Mark flag as permanent
                  </Link>{" "}
                  to keep it indefinitely.
                </Text>
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <HStack spacing={1}>
            {hasAllChecksPassed && (
              <IcoCheck color={allChecksPassedColor} size="md" />
            )}
            <Text
              color={hasAllChecksPassed ? allChecksPassedColor : "dimmed"}
              fontSize="xs"
              fontWeight={hasAllChecksPassed ? "medium" : "normal"}
            >
              {passedChecks}/{totalChecks} checks passed
            </Text>
          </HStack>
          <ButtonGroup>
            {archivingChecks &&
            archivingChecks.checksPassed &&
            !archivingChecks.isArchived ? (
              <Button size="sm" onClick={archiveNowHandler}>
                Archive now
              </Button>
            ) : archivingChecks && !archivingChecks.isArchived ? (
              <Button
                size="sm"
                variant="outline"
                onClick={archiveAnywayHandler}
              >
                Archive now anyway
              </Button>
            ) : (
              <Alert status="warning">The flag is currently archived</Alert>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ArchivingChecklist({
  archivingChecks,
  linearCleanupIssue,
  cleanupPromptLink,
  onManualCodeCleanupClick,
  onUndoManualCodeCleanupClick,
}: {
  archivingChecks: ArchivingChecks;
  linearCleanupIssue: Pick<LinearCleanupIssue, "url" | "details"> | null;
  cleanupPromptLink: string;
  onManualCodeCleanupClick: () => void;
  onUndoManualCodeCleanupClick: () => void;
}) {
  const { org, env } = useEnvironment();

  const { featureId } = useSlugParam("feature");

  const { latestPullRequest, isLoadingLatestPullRequest } =
    useLatestPullRequest(featureId!);

  const showManualCleanupActions =
    (!latestPullRequest ||
      (latestPullRequest.status !== "open" &&
        latestPullRequest.status !== "merged")) &&
    !archivingChecks.codeRefsRemovalStatus;
  const codeRefsMarkedClean = archivingChecks.codeRefsMarkedClean;

  return (
    <Flex as={Card} direction="column" padding={4} variant="filled">
      <CheckListItem
        isChecked={archivingChecks.isStale}
        title={`Stale - Rolled out to everyone for ${org.archiveAfterRolloutInDays} ${pluralize("day", org.archiveAfterRolloutInDays)}`}
      >
        <Text color="dimmed" fontSize="sm">
          {archivingChecks.rolledOutAt ? (
            <>
              Rolled out{" "}
              {formatRelativeTime(
                dayjs(archivingChecks.rolledOutAt),
                dayjs(),
                "day",
              )}
            </>
          ) : (
            "Not rolled out yet"
          )}
        </Text>
      </CheckListItem>
      <Divider my={4} />
      <CheckListItem
        isChecked={archivingChecks.codeRefsRemovalStatus}
        title="Remove flag code"
      >
        {linearCleanupIssue && (
          <LinearCleanupIssueLink
            issue={linearCleanupIssue.details}
            url={linearCleanupIssue.url}
            mb={2}
          />
        )}
        {!isLoadingLatestPullRequest && (
          <Flex direction="column" gap={2}>
            {codeRefsMarkedClean ? (
              <Flex direction="column" gap={2}>
                <Flex
                  align="center"
                  color="dimmed"
                  display="inline-flex"
                  flexWrap="wrap"
                  fontSize="sm"
                  gap={1}
                >
                  <Text>Marked as done</Text>
                  <Text>
                    {formatRelativeTime(
                      dayjs(codeRefsMarkedClean.markedAt),
                      dayjs(),
                      "day",
                    )}
                  </Text>
                  <Text>by</Text>
                  <ChangeActorDisplay
                    _avatar={{ size: "2xs" }}
                    actor={codeRefsMarkedClean.user}
                    display="inline-flex"
                    gap={1}
                  />{" "}
                </Flex>
                <Flex>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={onUndoManualCodeCleanupClick}
                  >
                    Undo
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <>
                {archivingChecks.githubIntegrationEnabled ? (
                  <>
                    {!archivingChecks.codeRefsScanCompleted &&
                    !latestPullRequest ? (
                      <HStack color="dimmed" spacing={2}>
                        <Spinner size="xs" speed="2.5s" />
                        <Text>Code reference scan running</Text>
                      </HStack>
                    ) : archivingChecks.codeRefsRemovalStatus &&
                      !latestPullRequest ? (
                      <Text color="dimmed">No code references found</Text>
                    ) : (
                      <Flex direction={"column"} gap={2.5} maxW="100%">
                        {latestPullRequest ? (
                          <>
                            <PullRequestLink
                              key={latestPullRequest.id}
                              number={latestPullRequest.number!}
                              status={latestPullRequest.status!}
                              title={latestPullRequest.title!}
                              url={latestPullRequest.url!}
                            />
                            {!showManualCleanupActions &&
                              !codeRefsMarkedClean && (
                                <Text color="dimmed">
                                  <Button
                                    size="sm"
                                    variant="link"
                                    onClick={onManualCodeCleanupClick}
                                  >
                                    Mark as manually done
                                  </Button>
                                </Text>
                              )}
                          </>
                        ) : (
                          <Text color="dimmed">
                            Code references have been found.
                          </Text>
                        )}
                        {showManualCleanupActions && (
                          <ButtonGroup>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={onManualCodeCleanupClick}
                            >
                              Mark as done
                            </Button>
                            {!linearCleanupIssue &&
                              archivingChecks.isStale &&
                              !archivingChecks.codeRefsRemovalStatus && (
                                <Button
                                  as="a"
                                  href={cleanupPromptLink}
                                  leftIcon={<IcoFeatureAutoCleanup size="sm" />}
                                  rel="noopener noreferrer"
                                  size="xs"
                                  target="_blank"
                                  variant="outline"
                                  width="fit-content"
                                >
                                  Open cleanup prompt
                                </Button>
                              )}
                          </ButtonGroup>
                        )}
                      </Flex>
                    )}
                  </>
                ) : (
                  <Flex direction={"column"} gap={2.5} maxW="100%">
                    {!org.autoCreateLinearCleanupIssue && (
                      <Text color="dimmed">
                        <Link
                          as={RouterLink}
                          to={GlobalSettingsUrl(env, "org-integrations")}
                        >
                          Integrate with GitHub
                        </Link>{" "}
                        to check for code references.
                      </Text>
                    )}
                    <ButtonGroup>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={onManualCodeCleanupClick}
                      >
                        Mark as done
                      </Button>
                      {!linearCleanupIssue &&
                        archivingChecks.isStale &&
                        !archivingChecks.codeRefsRemovalStatus && (
                          <Button
                            as="a"
                            href={cleanupPromptLink}
                            leftIcon={<IcoFeatureAutoCleanup size="sm" />}
                            rel="noopener noreferrer"
                            size="xs"
                            target="_blank"
                            variant="outline"
                            width="fit-content"
                          >
                            Open cleanup prompt
                          </Button>
                        )}
                    </ButtonGroup>
                  </Flex>
                )}
              </>
            )}
          </Flex>
        )}
      </CheckListItem>
      <Divider my={4} />
      <CheckListItem
        isChecked={archivingChecks.lastCheckStatus}
        title={`No access check last ${org.archiveAfterLastCheckInDays} ${pluralize("day", org.archiveAfterLastCheckInDays)}`}
      >
        <Text color="dimmed" fontSize="sm">
          {archivingChecks.lastCheckAt
            ? `Last check was ${formatRelativeTime(
                dayjs(archivingChecks.lastCheckAt),
                dayjs(),
                "day",
              )}`
            : "No access checks found"}
        </Text>
      </CheckListItem>
    </Flex>
  );
}
